import classnames from 'classnames'
import React from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import {
  Table,
  TableAlign,
  TableBody,
  TableHead,
  TableHeaderSortable,
  TableRow,
  getDefaultSortFromUrl,
  useTableSort,
} from '../../../components/Table'
import { SortByShape } from '../../../hooks/use-sort-data'
import { SortDirection, SortableValue } from '../../../types'
import { columnMap } from '../constants/columns'
import { ColumnId, Option, OptionRow, OptionsSubpage, RowKind, TableColumnDefinition } from '../types'
import { mergeOptionsBy } from '../utils/options'
import { getViewLink } from '../utils/query'
import { PriceRow } from './PriceRow'
import { OptionsTableCell, OptionsTableCellProps } from './TableColumn'

export interface ChainTableColumn extends Omit<TableColumnDefinition, 'id'> {
  /**
   * The sort hook internally compares `id` key, and because we have the same
   * columns on each side, we need them to be unique, so we keep the original id
   */
  id: string
  originalId: ColumnId
  /**
   * Used in sorting to distinguish between calls and puts columns
   */
  rowKind: RowKind
}

const CALL_OPTION_COLUMNS: ChainTableColumn[] = [
  ColumnId.LastClose,
  ColumnId.ChangeUsd,
  ColumnId.ChangePct,
  ColumnId.BidPrice,
  ColumnId.AskPrice,
  ColumnId.Volume,
  ColumnId.OpenInterest,
].map((id) => ({
  ...columnMap[id],
  originalId: id,
  id: `call_${columnMap[id].id}`,
  rowKind: RowKind.Call,
  className: classnames({
    'w-22 xl:w-auto': id === ColumnId.LastClose,
    'w-20 xl:w-auto': [ColumnId.ChangeUsd, ColumnId.OpenInterest].includes(id),
    'hidden xl:table-cell': id === ColumnId.ChangePct,
  }),
}))

const PUT_OPTION_COLUMNS: ChainTableColumn[] = CALL_OPTION_COLUMNS.map((option) => ({
  ...option,
  id: `put_${option.originalId}`,
  originalId: option.originalId,
  rowKind: RowKind.Put,
}))

const CALL_OPTION_GREEK_COLUMNS: ChainTableColumn[] = [
  ColumnId.LastClose,
  ColumnId.IV,
  ColumnId.Delta,
  ColumnId.Gamma,
  ColumnId.Theta,
  ColumnId.Vega,
  ColumnId.Rho,
].map((id) => ({
  ...columnMap[id],
  originalId: id,
  id: `call_${columnMap[id].id}`,
  rowKind: RowKind.Call,
  className: classnames({
    'w-22 xl:w-auto': id === ColumnId.LastClose,
    'w-20 xl:w-auto': [ColumnId.ChangeUsd, ColumnId.OpenInterest].includes(id),
    'hidden xl:table-cell': id === ColumnId.ChangePct,
  }),
}))

const PUT_OPTION_GREEK_COLUMNS: ChainTableColumn[] = CALL_OPTION_GREEK_COLUMNS.map((option) => ({
  ...option,
  id: `put_${option.originalId}`,
  originalId: option.originalId,
  rowKind: RowKind.Put,
}))

const FULL_COLUMNS_BREAKPOINT = 1280

function valueGetter(column: ChainTableColumn) {
  return (row: OptionRow) => {
    const customValueGetter = typeof column.getValueForSort === 'function' ? column.getValueForSort : undefined

    switch (column.rowKind) {
      case RowKind.OptionRow:
        if (customValueGetter) {
          return customValueGetter({ kind: RowKind.OptionRow, row })
        } else {
          return (row[column.originalId as keyof typeof row] as SortableValue) ?? null
        }
      case RowKind.Call:
        if (row.call && customValueGetter) {
          return customValueGetter({ kind: RowKind.Call, row: row.call })
        } else {
          return row.call?.[column.originalId as keyof Option] ?? null
        }
      case RowKind.Put:
        if (row.put && customValueGetter) {
          return customValueGetter({ kind: RowKind.Put, row: row.put })
        } else {
          return row.put?.[column.originalId as keyof Option] ?? null
        }
      default:
        return null
    }
  }
}

interface ChainTableProps {
  className?: string

  /**
   * Type of table, display either prices or volatility & greeks
   */
  subpage: OptionsSubpage

  /**
   * Options rows which are displayed in the table.
   */
  data: Option[]

  /**
   * `id` of the column which is displayed as the middle column between
   * calls and puts.
   *
   * @default ColumnId.StrikePrice
   */
  middleColumn?: ColumnId.StrikePrice | ColumnId.ExpirationDate

  /**
   * When enabled, a row with current price will be added to the table at the
   * position of the price. The row will only be visible when ordering by the
   * middle column.
   *
   * @default middleColumn === ColumnId.StrikePrice
   */
  isPriceRowEnabled?: boolean

  /**
   * Last close for current ticker. Used to add highlight on calls/puts and as
   * a row in the middle of the table which displays the current price.
   */
  lastClose: number | null

  /**
   * Time when the `lastClose` was valid. Displayed in current price row.
   */
  lastTime: number | null

  /**
   * Whether headers should become sticky
   */
  isHeaderSticky?: boolean
  headerClass?: string

  /**
   * Sorting key (column id, with optional dash prefix for descending)
   */
  tableOrder: string

  onSortChange?: (
    column: ChainTableColumn,
    sort: SortByShape<ChainTableColumn>,
    changeSort: (newSort: ChainTableColumn, direction?: SortDirection) => void
  ) => void
}

export function ChainTable({
  className,
  data,
  lastClose,
  lastTime,
  middleColumn = ColumnId.StrikePrice,
  isPriceRowEnabled = middleColumn === ColumnId.StrikePrice,
  isHeaderSticky,
  headerClass,
  subpage,
  tableOrder,
  onSortChange,
}: ChainTableProps) {
  const CALL_COLUMNS = subpage === OptionsSubpage.Volatility ? CALL_OPTION_GREEK_COLUMNS : CALL_OPTION_COLUMNS
  const PUT_COLUMNS = subpage === OptionsSubpage.Volatility ? PUT_OPTION_GREEK_COLUMNS : PUT_OPTION_COLUMNS
  const [columnsLength, setColumnsLength] = React.useState(CALL_COLUMNS.length)
  const [searchParams] = useSearchParams()
  const tickerLastClose = lastClose ?? 0
  const location = useLocation()

  React.useEffect(() => {
    const updateColumnsLength = () => {
      if (window.innerWidth >= FULL_COLUMNS_BREAKPOINT && columnsLength === CALL_COLUMNS.length - 1) {
        setColumnsLength(CALL_COLUMNS.length)
      } else if (window.innerWidth < FULL_COLUMNS_BREAKPOINT && columnsLength === CALL_COLUMNS.length) {
        setColumnsLength(CALL_COLUMNS.length - 1)
      }
    }

    updateColumnsLength()
    window.addEventListener('resize', updateColumnsLength)

    return () => {
      window.removeEventListener('resize', updateColumnsLength)
    }
  })

  const middleColumnDefinition = React.useMemo(
    () => ({
      ...columnMap[middleColumn],
      originalId: middleColumn,
      rowKind: RowKind.OptionRow,
    }),
    [middleColumn]
  )

  const allColumns = React.useMemo(
    () => [...CALL_COLUMNS, middleColumnDefinition, ...PUT_COLUMNS],
    [CALL_COLUMNS, PUT_COLUMNS, middleColumnDefinition]
  )

  const mergedData = React.useMemo(() => mergeOptionsBy(data, middleColumn), [data, middleColumn])
  const {
    data: tableRows,
    sort,
    changeSort,
  } = useTableSort({
    data: mergedData,
    columns: allColumns,
    defaultSort: getDefaultSortFromUrl(allColumns, tableOrder),
    valueGetter,
    key: location.key,
  })

  /**
   * We display current price as a row between strike prices
   */
  const currentPriceRow = React.useMemo(() => {
    const isVisible = isPriceRowEnabled && sort.column.id === middleColumnDefinition.id

    if (!isVisible) return { isVisible, rowIndex: -1 }

    const rowIndex =
      sort.direction === SortDirection.Ascending
        ? tableRows.findLastIndex((option) => option.strike < tickerLastClose)
        : tableRows.findLastIndex((option) => option.strike > tickerLastClose)

    return { isVisible, index: rowIndex }
  }, [isPriceRowEnabled, sort.column.id, sort.direction, middleColumnDefinition.id, tableRows, tickerLastClose])

  const priceRowProps = React.useMemo(
    () => ({
      lastClose: tickerLastClose,
      lastTime,
      leftSpan: columnsLength,
      rightSpan: columnsLength,
    }),
    [tickerLastClose, lastTime, columnsLength]
  )

  return (
    <Table className={classnames(className, 'table-fixed')} cellPadding={1}>
      <TableHead className={classnames(headerClass, { 'sticky z-20 bg-primary': isHeaderSticky })}>
        {CALL_COLUMNS.map((column) => (
          <TableHeaderSortable
            key={column.id}
            align={column.align}
            className={classnames(column.className, 'border-transparent first:border-l first:pl-2.5')}
            isActive={column.id === sort.column.id}
            direction={sort.direction}
            onClick={() => {
              typeof onSortChange === 'function' ? onSortChange(column, sort, changeSort) : changeSort(column)
            }}
          >
            {column.name}
          </TableHeaderSortable>
        ))}

        <th className="w-1" />
        <TableHeaderSortable
          align={TableAlign.Center}
          isActive={middleColumnDefinition.id === sort.column.id}
          direction={sort.direction}
          onClick={() => {
            typeof onSortChange === 'function'
              ? onSortChange(middleColumnDefinition, sort, changeSort)
              : changeSort(middleColumnDefinition)
          }}
          className="w-28"
        >
          {middleColumnDefinition.name}
        </TableHeaderSortable>
        <th className="w-1" />

        {PUT_COLUMNS.map((column) => (
          <TableHeaderSortable
            key={column.id}
            align={column.align}
            className={classnames(column.className, 'border-transparent last:border-r last:pr-2.5')}
            isActive={column.id === sort.column.id}
            direction={sort.direction}
            onClick={() => {
              typeof onSortChange === 'function' ? onSortChange(column, sort, changeSort) : changeSort(column)
            }}
          >
            {column.name}
          </TableHeaderSortable>
        ))}
      </TableHead>

      {currentPriceRow.isVisible && currentPriceRow.index < 0 && (
        <TableBody>
          <PriceRow {...priceRowProps} />
        </TableBody>
      )}

      <TableBody>
        {tableRows.map((option, index) => {
          const isCurrentPriceRow = currentPriceRow.isVisible && currentPriceRow.index === index
          const showPriceAfter = isCurrentPriceRow && currentPriceRow.index < tableRows.length - 1
          const columnClass = classnames({ 'border-b': showPriceAfter })
          const middleLink = getViewLink(middleColumn, searchParams, option)

          const row = (
            <TableRow className="styled-row is-hover-borders" key={`${option.exDate}-${option.strike}`}>
              {option.call ? (
                <ChainOptionRow
                  columns={CALL_COLUMNS}
                  kind={RowKind.Call}
                  option={option.call}
                  isHighlighted={option.strike < tickerLastClose}
                  className={columnClass}
                />
              ) : (
                <OptionsTableCell colSpan={CALL_COLUMNS.length} isBorderVisible={!!tableRows[index - 1]?.call} />
              )}

              <td className="is-separator" />
              <OptionsTableCell isFirst isLast align={TableAlign.Center} className={columnClass}>
                <Link to={`?${middleLink}`} className="tab-link">
                  {middleColumnDefinition.renderColumn({ kind: RowKind.OptionRow, row: option })}
                </Link>
              </OptionsTableCell>
              <td className="is-separator" />

              {option.put ? (
                <ChainOptionRow
                  columns={PUT_COLUMNS}
                  kind={RowKind.Put}
                  option={option.put}
                  isHighlighted={option.strike > tickerLastClose}
                  className={columnClass}
                />
              ) : (
                <OptionsTableCell colSpan={PUT_COLUMNS.length} isBorderVisible={!!tableRows[index - 1]?.put} />
              )}
            </TableRow>
          )

          if (showPriceAfter) {
            return (
              <React.Fragment key={`${option.exDate}-${option.strike}`}>
                {row}
                {showPriceAfter && <PriceRow {...priceRowProps} />}
              </React.Fragment>
            )
          }

          return row
        })}
      </TableBody>

      {currentPriceRow.isVisible && currentPriceRow.index === tableRows.length - 1 && (
        <TableBody>
          <PriceRow {...priceRowProps} />
        </TableBody>
      )}
    </Table>
  )
}

interface ChainOptionRowProps extends Omit<OptionsTableCellProps, 'kind'> {
  columns: ChainTableColumn[]
  kind: RowKind.Call | RowKind.Put
  option: Option
  isHighlighted?: boolean
}

function ChainOptionRow({ columns, kind, option, isHighlighted, className, ...props }: ChainOptionRowProps) {
  return (
    <>
      {columns.map((column, index) => {
        const isFirst = index === 0
        const isLast = index === columns.length - 1

        return (
          <OptionsTableCell
            {...props}
            key={column.id}
            isHighlighted={kind === RowKind.Call ? isFirst && isHighlighted : isLast && isHighlighted}
            isFirst={isFirst}
            isLast={isLast}
            align={column.align}
            className={classnames(className, column.className)}
          >
            {column.renderColumn({ kind, row: option })}
          </OptionsTableCell>
        )
      })}
    </>
  )
}
